// UserSetting.js - 完整版本 (包含 Save Permissions)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Container } from '../style-comps/Common.styles';
import ConfirmResetPopup from '../components/ConfirmResetPopup';

import {
    GlobalStyle,
    LeftContainer,
    Title,
    Subtitle,
    RightContainer,
    SectionTitle,
    UserDetailsContainer,
    InputGroup,
    InputLabel,
    BtnWrapper,
    SaveButton,
    ListingCard,
    ListingCardLink,
    ListingCardSub,
    BackButton,
    PermissionSection,
    PermissionCategory,
    CategoryHeader,
    CategoryTitle,
    PermissionList,
    PermissionItem,
    PermissionLabel,
    PermissionDescription,
    ErrorPopup,
    CloseButton,
    SyncButton,
    SyncIconImg,
    ListingGrid,
    ListingCardDeleteBtn,
    NoListingText,
    PasswordUpdatedAt,
    RoleList,
    RoleLabel,
    RoleCheckbox,
    RoleName,
    CurrentRolesBox,
    PermissionPanel,
    PermissionBadge,
    PermissionCheckbox,
    PermissionItemInner,
    PermissionItemContent,
    CategoryHeaderLeft,
    CategoryCountBadge,
    ChevronIcon,
    ErrorText,
    WarningText,
    SuccessText,
    UpdatingText,
    PopupOverlay,
    PopupBox,
    PopupTitle,
    PopupMessage,
    PopupActions,
    PopupCancelBtn,
    PopupConfirmBtn,
    InputFieldRow
} from '../style-comps/UserSetting.styles';
import Input from '../components/Input';
import {
    getListingsByUserId,
    getListingById,
    adminSyncUserById,
    syncAdminUserListingsWithUserData,
    resetUserPassword,
    getUserById,
    getUserPermissions,
    getUserRoles,
    addUserRole,
    removeUserRole,
    updateUserPermissions,
    deleteUserPermission,
    getRoles,
    getAllPermissions,
    removeUserPermissionsBatch,
    deleteUserListingById,
    addUserListingsBatch,
} from '../services/api';
import SyncIcon from '../Assets/arrow-round-left.svg';

const formatDate = (isoString) => {
    if (!isoString) return "";

    let dateString = isoString;
    if (isoString.includes('T') && !isoString.includes('Z') && !isoString.includes('+')) {
        dateString = isoString + 'Z';
    }

    const date = new Date(dateString);

    const formatter = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    const parts = formatter.formatToParts(date);
    const getValue = (type) => parts.find(p => p.type === type)?.value;

    return `${getValue('year')}-${getValue('month')}-${getValue('day')} ${getValue('hour')}:${getValue('minute')} (Sydney Time)`;
};


const ChevronRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);


const UserSetting = () => {
    const navigate = useNavigate();
    const { id: rawId } = useParams();
    const id = rawId?.replace(/\D/g, '');
    const { state } = useLocation();
    const passedUser = state?.user || null;
    const [sp] = useSearchParams();
    const fromParam = sp.get('from') || '';
    const fromQuery = state?.fromQuery || fromParam;
    const userRole = JSON.parse(localStorage.getItem('roles'))?.[0] || '';

    // ── 權限治理：SuperAdmin 這個 Role 選項只有 SuperAdmin 本人能在這裡看到 / 勾選 ──
    // 非 SuperAdmin 一律不顯示此選項，避免一般 Admin 透過 UserSetting 頁面把使用者升成 SuperAdmin。
    // 若真的要指派 SuperAdmin，只能在專屬的 Roles & Permissions 頁面（RolesPermissions.js）由 SuperAdmin 本人操作。
    const currentUserRoles = JSON.parse(localStorage.getItem('roles') || '[]');
    const isSuperAdminUser = currentUserRoles.some((r) => (r || '').toLowerCase() === 'superadmin');
    const [isSyncingListings, setIsSyncingListings] = useState(false);
    const [addListingInput, setAddListingInput] = useState('');
    const [isAddingListing, setIsAddingListing] = useState(false);
    const [passwordUpdatedAt, setPasswordUpdatedAt] = useState("");
    const [latestRoleNames, setLatestRoleNames] = useState([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        password: '',
        UserName: '',
    });

    const [errors, setErrors] = useState({});
    const [listings, setListings] = useState([]);
    const [hoveredId, setHoveredId] = useState(null);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [dirty, setDirty] = useState(false);

    // 權限相關 state
    const [userPermissions, setUserPermissions] = useState({});
    const [originalPermissions, setOriginalPermissions] = useState({}); // 儲存原始權限
    const [removedPermissions, setRemovedPermissions] = useState(new Set()); // 追蹤要移除的權限
    const [permissionDetails, setPermissionDetails] = useState([]);

    // Role 相關 state
    const [availableRoles, setAvailableRoles] = useState([]);
    const [userRoleIds, setUserRoleIds] = useState([]);
    const [originalRoleIds, setOriginalRoleIds] = useState([]);
    const [isUpdatingRoles, setIsUpdatingRoles] = useState(false);
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);
    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [rolesSaved, setRolesSaved] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState(['Listings', 'Reports']);
    const [allPermissions, setAllPermissions] = useState([]);
    const [roleSourcePermissions, setRoleSourcePermissions] = useState(new Map());
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showDeleteListingPopup, setShowDeleteListingPopup] = useState(false);
    const [pendingDeleteListingId, setPendingDeleteListingId] = useState(null);
    const [isSyncingUser, setIsSyncingUser] = useState(false);

    useEffect(() => {
        if (userRole !== 'Admin' && !isSuperAdminUser) {
            navigate('/dashboard');
        }
    }, [userRole, isSuperAdminUser, navigate]);


    const fetchAndSetListings = async () => {
        const summaries = await getListingsByUserId(id);
        const parents = summaries.filter(l => l.ParentListingId === null);
        const detailed = await Promise.all(parents.map(l => getListingById(l.Id)));
        setListings(detailed);
    };

    const handleDeleteListing = async (listingId) => {
        setPendingDeleteListingId(listingId);
        setShowDeleteListingPopup(true);
    };

    const confirmDeleteListing = async () => {
        setShowDeleteListingPopup(false);
        try {
            await deleteUserListingById(id, pendingDeleteListingId);
            await fetchAndSetListings();
        } catch (err) {
            console.error('❌ 刪除 Listing 失敗:', err);
            setErrorMessage('Failed to remove listing. Please try again.');
            setShowError(true);
        } finally {
            setPendingDeleteListingId(null);
        }
    };

    useEffect(() => {
        if (!id) {
            console.error("Missing user ID, redirecting...");
            navigate('/dashboard/admin-dashboard');
            return;
        }


        const init = async () => {
            try {

                // 0. 取得所有可用 Roles
                console.log('Fetching available roles...');
                const allRoles = await getRoles();
                const rolesArr = Array.isArray(allRoles) ? allRoles : allRoles?.data ?? [];
                setAvailableRoles(rolesArr.map(r => ({ id: r.Id, name: r.Name })));
                console.log('Available roles loaded:', rolesArr);


                // 1. 取得基本使用者資料
                let userData = passedUser;

                if (!userData) {
                    console.log('Fetching user data from API...');
                    userData = await getUserById(id);
                }

                setFormData({
                    firstName: userData.FirstName || '',
                    lastName: userData.LastName || '',
                    password: '',
                    UserName: userData.UserName || '',
                });

                setPasswordUpdatedAt(formatDate(userData.UpdatedAt));



                // 2. 取得使用者的 Roles
                console.log('Fetching user roles...');
                const rolesData = await getUserRoles(id);
                console.log('User roles data:', rolesData);

                const roleIds = rolesData.map(r => r.RoleId);
                const roleNames = rolesData.map(r => r.RoleName || r.Name).filter(Boolean);

                setUserRoleIds(roleIds);
                setOriginalRoleIds(roleIds);
                setLatestRoleNames(roleNames);
                console.log('User RoleIds loaded:', roleIds);

                // 3. 取得使用者的 Permissions
                console.log('Fetching user permissions...');
                const [permissionsData, allPerms] = await Promise.all([
                    getUserPermissions(id),
                    getAllPermissions()
                ]);
                console.log('Permissions data:', permissionsData);

                // 所有可用權限清單
                const permsArr = Array.isArray(allPerms) ? allPerms : allPerms?.data ?? [];
                setAllPermissions(permsArr);

                if (permissionsData.Permissions) {
                    setUserPermissions(permissionsData.Permissions);
                    setOriginalPermissions(permissionsData.Permissions);
                    setRemovedPermissions(new Set());
                    console.log('User Permissions loaded:', permissionsData.Permissions);
                }

                if (permissionsData.Details) {
                    setPermissionDetails(permissionsData.Details);

                    // 找出哪些權限是 Role 帶來的（Source 包含 "Role:"）
                    const rolePermsMap = new Map(
                        permissionsData.Details
                            .filter(d => d.Source && d.Source.startsWith('Role:'))
                            .map(d => [d.PermissionName, d.Source.replace('Role:', '')])
                    );
                    setRoleSourcePermissions(rolePermsMap);
                    console.log('Role-sourced permissions:', Array.from(rolePermsMap));
                }

            } catch (err) {
                console.error("無法取得使用者資料:", err);
                navigate('/dashboard/admin-dashboard');
                return;
            }

            // 4. 取得 Listings
            try {
                await fetchAndSetListings();
            } catch (err) {
                console.warn("無法取得使用者的 listing:", err);
                setListings([]);
            }
        };


        init();
    }, [id, navigate, passedUser]);


    const handleSyncUser = async () => {
        if (isSyncingUser) return;
        setIsSyncingUser(true);
        try {
            await adminSyncUserById(id);
            setDirty(true);
            setErrorMessage("User synced successfully!");
            setShowError(true);
        } catch (error) {
            console.error("Failed to sync user", error);
            setErrorMessage("Failed to sync user. Please try again later.");
            setShowError(true);
        } finally {
            setIsSyncingUser(false);
        }
    };

    const handleSyncUserListings = async () => {
        if (isSyncingListings) return;
        setIsSyncingListings(true);
        try {
            await syncAdminUserListingsWithUserData();
            setDirty(true);
            setErrorMessage("User listings synced successfully!");
            setShowError(true);

            await fetchAndSetListings();
        } catch (error) {
            console.error("❌ Failed to sync user listings. Please try again later", error);
            setErrorMessage("Something went wrong. Please try again later.");
            setShowError(true);
        } finally {
            setIsSyncingListings(false);
        }
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    const handleAddListing = async () => {
        const raw = addListingInput.trim();
        if (!raw) return;

        const listingIds = raw
            .split(/[\s,]+/)
            .map(v => Number(v))
            .filter(v => !isNaN(v) && v > 0);

        if (listingIds.length === 0) {
            setErrorMessage('Please enter a valid Listing ID.');
            setShowError(true);
            return;
        }

        setIsAddingListing(true);
        try {
            // 現有的 parent listing IDs
            const existingIds = new Set(listings.map(l => l.Id));

            const resolvedIds = await Promise.all(
                listingIds.map(async (lid) => {
                    try {
                        const listing = await getListingById(lid);
                        // 如果是 child，取 parent ID
                        const targetId = listing?.ParentListingId ?? lid;

                        // 檢查 parent 是否已在列表
                        if (existingIds.has(targetId)) {
                            console.log(`⚠️ Listing ${targetId} already exists, skipping.`);
                            return null; // 標記為跳過
                        }

                        if (listing?.ParentListingId) {
                            console.log(`ℹ️ Listing ${lid} is a child → using parent ${targetId}`);
                        }

                        return targetId;
                    } catch {
                        return lid;
                    }
                })
            );

            // 過濾掉 null（已存在的）並去重
            const uniqueIds = [...new Set(resolvedIds.filter(Boolean))];

            if (uniqueIds.length === 0) {
                setErrorMessage('This listing is already added.');
                setShowError(true);
                return;
            }

            const result = await addUserListingsBatch(id, uniqueIds);
            setAddListingInput('');
            setErrorMessage(result.Message || 'Listing(s) added successfully!');
            setShowError(true);
            await fetchAndSetListings();
        } catch (err) {
            console.error('❌ Add Listing 失敗:', err);
            setErrorMessage(err.response?.data?.message || 'Failed to add listing. Please try again.');
            setShowError(true);
        } finally {
            setIsAddingListing(false);
        }
    };

    const handlePermissionToggle = (permissionKey) => {
        setUserPermissions(prev => {
            const currentValue = prev[permissionKey];
            const wasOriginallyPresent = originalPermissions[permissionKey];
            const isFromRole = roleSourcePermissions.has(permissionKey); // ← 加這個

            if (!currentValue) {
                // 勾選 → 新增權限
                setRemovedPermissions(prevSet => {
                    const newSet = new Set(prevSet);
                    newSet.delete(permissionKey);
                    return newSet;
                });
                return { ...prev, [permissionKey]: "Own" };
            } else {
                // 取消勾選 → 移除權限
                // 原本有（無論是直接賦予還是 Role 繼承）都要加進 removedPermissions
                if (wasOriginallyPresent || isFromRole) {
                    setRemovedPermissions(prevSet => new Set([...prevSet, permissionKey]));
                }
                const newPerms = { ...prev };
                delete newPerms[permissionKey];
                return newPerms;
            }
        });
        setDirty(true);
    };

    // 處理 Role Checkbox 的改變
    const handleRoleToggle = (roleId) => {
        setUserRoleIds(prev =>
            prev.includes(roleId)
                ? prev.filter(r => r !== roleId)
                : [...prev, roleId]
        );
        setRolesSaved(false);
    };

    // 儲存 Role 變更
    const handleSaveRoles = async () => {
        if (userRoleIds.length === 0) {
            setErrorMessage("At least one role must be assigned to this user.");
            setShowError(true);
            return;
        }

        setIsSavingRoles(true);

        try {
            // 計算要新增的 Role（目前有，原本沒有）
            const toAdd = userRoleIds.filter(r => !originalRoleIds.includes(r));
            // 計算要移除的 Role（原本有，目前沒有）
            const toRemove = originalRoleIds.filter(r => !userRoleIds.includes(r));

            console.log('➕ Roles to add:', toAdd);
            console.log('🗑️ Roles to remove:', toRemove);

            for (const roleId of toAdd) {
                await addUserRole(id, roleId);
                console.log(`✅ Role ${roleId} added`);
            }

            for (const roleId of toRemove) {
                await removeUserRole(id, roleId);
                console.log(`✅ Role ${roleId} removed`);
            }

            // 更新 originalRoleIds
            const latestRoles = await getUserRoles(id);
            const latestRoleIds = latestRoles.map(r => r.RoleId);
            const latestNames = latestRoles.map(r => r.RoleName || r.Name).filter(Boolean);

            setUserRoleIds(latestRoleIds);
            setOriginalRoleIds(latestRoleIds);
            setLatestRoleNames(latestNames);

            // 重新載入 Permissions（Role 變了，權限清單要更新）
            console.log('📡 Reloading permissions...');
            const permissionsData = await getUserPermissions(id);

            if (permissionsData.Permissions) {
                setUserPermissions(permissionsData.Permissions);
                setOriginalPermissions(permissionsData.Permissions);
                setRemovedPermissions(new Set());
            }

            if (permissionsData.Details) {
                setPermissionDetails(permissionsData.Details);
                const rolePermsMap = new Map(
                    permissionsData.Details
                        .filter(d => d.Source && d.Source.startsWith('Role:'))
                        .map(d => [d.PermissionName, d.Source.replace('Role:', '')])
                );
                setRoleSourcePermissions(rolePermsMap);
            }

            setRolesSaved(true);
            setDirty(true);
            console.log('✅ Roles saved successfully');



        } catch (error) {
            console.error("Failed to save roles:", error);
            setErrorMessage("Failed to save roles. Please try again.");
            setShowError(true);
        } finally {
            setIsSavingRoles(false);
        }
    };



    // ✨ 儲存 Additional Permissions
    const handleSavePermissions = async () => {
        setIsSavingPermissions(true);

        try {
            console.log('========================================');
            console.log('💾 Saving additional permissions...');
            console.log('========================================');
            console.log('Current Permissions:', userPermissions);
            console.log('Original Permissions:', originalPermissions);
            console.log('Removed Permissions:', Array.from(removedPermissions));

            // Step 1: 移除權限 ( batch POST + Scope 0)
            if (removedPermissions.size > 0) {
                console.log('\nRemoving permissions via batch (Scope: 0)...');
                try {
                    await removeUserPermissionsBatch(id, Array.from(removedPermissions));
                    console.log('Permissions removed successfully');
                } catch (error) {
                    console.error('Failed to remove permissions:', error.response?.data || error.message);
                }
            }

            // Step 2: 新增權限 (使用 POST batch API)
            // 只送新增的權限,不送原本就有的
            const newPermissions = {};
            for (const [key, value] of Object.entries(userPermissions)) {
                if (!originalPermissions[key]) {
                    newPermissions[key] = value;
                }
            }

            if (Object.keys(newPermissions).length > 0) {
                console.log('\nAdding new permissions:', newPermissions);
                await updateUserPermissions(id, newPermissions);
                console.log('New permissions added successfully');
            } else {
                console.log('\n No new permissions to add');
            }

            // Step 3: 重新載入最新的權限資料
            console.log('\nReloading permissions from server...');
            const permissionsData = await getUserPermissions(id);

            console.log('\nServer returned:');
            console.log('Permissions:', permissionsData.Permissions);

            if (permissionsData.Permissions) {
                setUserPermissions(permissionsData.Permissions);
                setOriginalPermissions(permissionsData.Permissions);
                setRemovedPermissions(new Set()); // 清空移除列表
                console.log('State updated with reloaded permissions');
            }

            if (permissionsData.Details) {
                setPermissionDetails(permissionsData.Details);
            }

            console.log('========================================');
            setErrorMessage("Permissions saved successfully!");
            setShowError(true);
            setDirty(true);

        } catch (error) {
            console.error("========================================");
            console.error("❌ Failed to save permissions:", error);
            console.error("❌ Error response:", error.response?.data);
            console.error("========================================");

            // 顯示詳細錯誤訊息
            const errMsg = error.response?.data?.message
                || error.message
                || "Failed to save permissions. Please try again.";

            setErrorMessage(errMsg);
            setShowError(true);

        } finally {
            setIsSavingPermissions(false);
        }
    };

    // 取得使用者當前的 Role 名稱
    const getCurrentRoleNames = () => {
        return availableRoles
            .filter(role => userRoleIds.includes(role.id))
            .map(role => role.name)
            .join(', ');
    };

    // 檢查是否有未儲存的變更
    const hasUnsavedChanges = () => {
        const permissionsChanged = JSON.stringify(userPermissions) !== JSON.stringify(originalPermissions);
        const hasRemovals = removedPermissions.size > 0;
        return permissionsChanged || hasRemovals;
    };

    return (
        <Container>
            <GlobalStyle />
            {/* ========== LEFT COLUMN ========== */}
            <LeftContainer>
                <div>
                    <Title>User Setting</Title>
                    <Subtitle>You can easily update user password, listings, and permissions here.</Subtitle>
                </div>
                <div>
                    <BackButton
                        onClick={() => {
                            navigate(`/dashboard/userlisting${fromQuery ? `?${fromQuery}` : ''}`, {
                                state: dirty
                                    ? {
                                        shouldRefetch: true,
                                        changedUserId: id,
                                        updatedRoles: latestRoleNames
                                    }
                                    : undefined
                            });
                        }}
                    >
                        Back to List
                    </BackButton>
                </div>

                {/* User Info Section */}
                <div style={{ marginTop: '30px' }}>
                    <SectionTitle>
                        User Info
                        <SyncButton onClick={handleSyncUser} disabled={isSyncingUser} $syncing={isSyncingUser} title="Sync User">
                            <SyncIconImg src={SyncIcon} alt="Sync Icon" $syncing={isSyncingUser} />
                        </SyncButton>
                    </SectionTitle>

                    <UserDetailsContainer>
                        <InputGroup>
                            <InputLabel>Email</InputLabel>
                            <Input name="UserName" value={formData.UserName} readOnly />
                        </InputGroup>
                        <InputFieldRow>
                            <InputGroup style={{ flex: 1 }}>
                                <InputLabel>First Name</InputLabel>
                                <Input name="firstName" value={formData.firstName} disabled />
                            </InputGroup>
                            <InputGroup style={{ flex: 1 }}>
                                <InputLabel>Last Name</InputLabel>
                                <Input name="lastName" value={formData.lastName} disabled />
                            </InputGroup>
                        </InputFieldRow>

                    </UserDetailsContainer>
                </div>

                {/* Reset Password Section */}
                <div style={{ marginTop: '20px' }}>
                    <SectionTitle style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        Reset Password
                        {passwordUpdatedAt && (
                            <PasswordUpdatedAt>Latest Updated At: {passwordUpdatedAt}</PasswordUpdatedAt>
                        )}
                    </SectionTitle>
                    <BtnWrapper style={{ marginTop: '16px' }}>
                        <SaveButton
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowConfirmPopup(true);
                            }}
                        >
                            Reset Password
                        </SaveButton>
                    </BtnWrapper>
                </div>
            </LeftContainer>

            {/* ========== RIGHT COLUMN ========== */}
            <RightContainer>
                {/* User Listing Section */}
                <SectionTitle style={{ marginBottom: 0 }}>
                    User Property Listing
                    <SyncButton onClick={handleSyncUserListings} disabled={isSyncingListings} $syncing={isSyncingListings} title="Sync User Listings">
                        <SyncIconImg src={SyncIcon} alt="Sync Icon" $syncing={isSyncingListings} />
                    </SyncButton>


                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Input
                            placeholder="Listing ID"
                            value={addListingInput}
                            onChange={(e) => setAddListingInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddListing()}
                            style={{ width: '110px', padding: '4px 8px', height: '32px' }}
                        />
                        <SaveButton
                            type="button"
                            onClick={handleAddListing}
                            disabled={isAddingListing || !addListingInput.trim()}
                            style={{
                                opacity: (isAddingListing || !addListingInput.trim()) ? 0.5 : 1,
                                whiteSpace: 'nowrap',
                                padding: '4px 14px',
                                height: '32px',
                                fontSize: '14px',
                                width: 'auto',
                            }}
                        >
                            {isAddingListing ? 'Adding...' : 'Add Listing'}
                        </SaveButton>
                    </div>
                </SectionTitle>


                {listings.length === 0 ? (
                    <NoListingText>No Property List</NoListingText>
                ) : (
                    <ListingGrid>
                        {listings.map((listing) => (
                            <ListingCard key={listing.Id}>
                                <ListingCardDeleteBtn onClick={() => handleDeleteListing(listing.Id)} title="Remove this Listing">
                                    ×
                                </ListingCardDeleteBtn>
                                <ListingCardLink
                                    href={`https://app.hostify.com/listings/view/${listing.Id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onMouseEnter={() => setHoveredId(listing.Id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    $hovered={hoveredId === listing.Id}
                                >
                                    {listing.Name}
                                </ListingCardLink>
                                <ListingCardSub>{listing.NickName}</ListingCardSub>
                            </ListingCard>
                        ))}
                    </ListingGrid>
                )}

                {/* User Permission Section */}
                <SectionTitle>User Role</SectionTitle>
                <PermissionSection>
                    <InputGroup>
                        <InputLabel>Basic Role (Multiple choices allowed. Select at least one role.)</InputLabel>
                        <RoleList>
                            {availableRoles
                                .filter(role => isSuperAdminUser || (role.name || '').toLowerCase() !== 'superadmin')
                                .map(role => (
                                <RoleLabel key={role.id} $selected={userRoleIds.includes(role.id)} $disabled={isUpdatingRoles}>
                                    <RoleCheckbox
                                        checked={userRoleIds.includes(role.id)}
                                        onChange={() => handleRoleToggle(role.id)}
                                        disabled={isUpdatingRoles}
                                    />
                                    <RoleName $selected={userRoleIds.includes(role.id)}>{role.name}</RoleName>
                                </RoleLabel>
                            ))}
                        </RoleList>

                        {/* 顯示當前 Roles */}
                        {/* Save Role 按鈕 */}
                        <BtnWrapper style={{ marginTop: '16px' }}>
                            <SaveButton
                                type="button"
                                onClick={handleSaveRoles}
                                disabled={isSavingRoles || userRoleIds.length === 0}
                                style={{
                                    opacity: (isSavingRoles || userRoleIds.length === 0) ? 0.5 : 1
                                }}
                            >
                                {isSavingRoles ? 'Saving...' : 'Save Roles'}
                            </SaveButton>

                            {userRoleIds.length === 0 && (
                                <ErrorText>At least one role is required.</ErrorText>
                            )}
                            {rolesSaved && !isSavingRoles && (
                                <SuccessText>Roles saved. You can now update permissions.</SuccessText>
                            )}
                        </BtnWrapper>
                        {userRoleIds.length > 0 && (
                            <CurrentRolesBox>
                                Current Roles: <strong style={{ color: '#61cbc9' }}>{getCurrentRoleNames()}</strong>
                                {isUpdatingRoles && <UpdatingText>Updating...</UpdatingText>}
                            </CurrentRolesBox>
                        )}
                    </InputGroup>

                    {/* Permission Categories */}
                    <PermissionPanel $locked={!rolesSaved}>
                        <InputLabel style={{ marginBottom: '12px', display: 'block' }}>
                            Permission Additional
                            {hasUnsavedChanges() && (
                                <span style={{ color: '#f59e0b', marginLeft: '8px', fontSize: '11px' }}>
                                    (Unsaved Changes)
                                </span>
                            )}
                            {removedPermissions.size > 0 && (
                                <span style={{ color: '#ef4444', marginLeft: '8px', fontSize: '11px' }}>
                                    ({removedPermissions.size} permission(s) will be removed)
                                </span>
                            )}
                        </InputLabel>

                        {/* 依 Category 分組顯示 */}
                        {Object.entries(
                            allPermissions.reduce((acc, perm) => {
                                const cat = perm.Category || 'General';
                                if (!acc[cat]) acc[cat] = [];
                                acc[cat].push(perm);
                                return acc;
                            }, {})
                        ).map(([categoryName, perms]) => {
                            const isExpanded = expandedCategories.includes(categoryName);
                            const enabledCount = perms.filter(p => userPermissions[p.Name]).length;
                            const totalCount = perms.length;

                            return (
                                <PermissionCategory key={categoryName}>
                                    <CategoryHeader
                                        onClick={() => toggleCategory(categoryName)}
                                        $expanded={isExpanded}
                                    >
                                        <CategoryHeaderLeft>
                                            <CategoryTitle>{categoryName}</CategoryTitle>
                                            <CategoryCountBadge>({enabledCount}/{totalCount})</CategoryCountBadge>
                                        </CategoryHeaderLeft>
                                        <ChevronIcon $expanded={isExpanded}>
                                            <ChevronRight />
                                        </ChevronIcon>
                                    </CategoryHeader>

                                    {isExpanded && (
                                        <PermissionList>
                                            {perms.map((perm) => {
                                                const isFromRole = roleSourcePermissions.has(perm.Name);
                                                const fromRoleName = roleSourcePermissions.get(perm.Name);
                                                const isEnabled = !!userPermissions[perm.Name];

                                                return (
                                                    <PermissionItem key={perm.Name}>
                                                        <PermissionItemInner>
                                                            <PermissionCheckbox
                                                                checked={isEnabled}
                                                                onChange={() => handlePermissionToggle(perm.Name)}
                                                                disabled={isUpdatingRoles || isSavingPermissions}
                                                                $disabled={isUpdatingRoles || isSavingPermissions}
                                                            />
                                                            <PermissionItemContent>
                                                                <PermissionLabel $enabled={isEnabled}>
                                                                    {perm.DisplayName || perm.Name}
                                                                    {isFromRole && <PermissionBadge>(from Role: {fromRoleName})</PermissionBadge>}
                                                                    {isEnabled && !isFromRole && <PermissionBadge>({userPermissions[perm.Name]})</PermissionBadge>}
                                                                </PermissionLabel>
                                                                <PermissionDescription>{perm.Description}</PermissionDescription>
                                                            </PermissionItemContent>
                                                        </PermissionItemInner>
                                                    </PermissionItem>
                                                );
                                            })}
                                        </PermissionList>
                                    )}
                                </PermissionCategory>
                            );
                        })}
                    </PermissionPanel>

                    {/* Save Button */}
                    <BtnWrapper style={{ marginTop: '24px' }}>
                        <SaveButton
                            type="button"
                            onClick={handleSavePermissions}
                            disabled={isUpdatingRoles || isSavingPermissions || !hasUnsavedChanges()}
                            style={{
                                opacity: (!hasUnsavedChanges() || isUpdatingRoles || isSavingPermissions) ? 0.5 : 1
                            }}
                        >
                            {isSavingPermissions ? 'Saving...' : 'Save Additional Permissions'}
                        </SaveButton>

                        {hasUnsavedChanges() && !isSavingPermissions && (
                            <div>
                                {removedPermissions.size > 0 && (
                                    <ErrorText>{removedPermissions.size} permission(s) will be removed</ErrorText>
                                )}
                                <WarningText>You have unsaved changes</WarningText>
                            </div>
                        )}
                    </BtnWrapper>
                </PermissionSection>
            </RightContainer>

            {/* Confirm Reset Popup */}
            {showConfirmPopup && (
                <ConfirmResetPopup
                    onConfirm={async () => {
                        const token = localStorage.getItem("authToken");
                        try {
                            const result = await resetUserPassword(id);
                            console.log("✅ 密碼重設成功");

                            const updatedUserData = await getUserById(id, token);

                            setFormData({
                                firstName: updatedUserData.FirstName || '',
                                lastName: updatedUserData.LastName || '',
                                password: '',

                            });
                            setPasswordUpdatedAt(formatDate(updatedUserData.UpdatedAt));

                            setDirty(true);

                            return result;
                        } catch (err) {
                            console.error("❌ 密碼重設失敗:", err.response?.data || err.message);
                            throw err;
                        }
                    }}
                    onCancel={() => setShowConfirmPopup(false)}
                />
            )}
            {showError && (
                <ErrorPopup show={showError}>
                    <div>{errorMessage}</div>
                    <CloseButton onClick={() => setShowError(false)}>×</CloseButton>
                </ErrorPopup>
            )}

            {/* Confirm Delete Listing Popup */}
            {showDeleteListingPopup && (
                <PopupOverlay>
                    <PopupBox>
                        <PopupTitle>Remove Listing</PopupTitle>
                        <PopupMessage>Are you sure you want to remove this listing from the user?</PopupMessage>
                        <PopupActions>
                            <PopupCancelBtn onClick={() => { setShowDeleteListingPopup(false); setPendingDeleteListingId(null); }}>
                                Cancel
                            </PopupCancelBtn>
                            <PopupConfirmBtn onClick={confirmDeleteListing}>
                                Remove
                            </PopupConfirmBtn>
                        </PopupActions>
                    </PopupBox>
                </PopupOverlay>
            )}
        </Container>
    );
};

export default UserSetting;